import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import { Loading } from 'components/loading/Loading';
import UsersTable from 'components/UsersTable';
import React, { useEffect, useState } from 'react';
import { fetchUsersForAccount } from 'services';
import { addUserToAccount, fetchAccountDetails, removeUserFromAccount, sendPendingPaymentEmailToAccount, updateAccount } from 'services/api/accounts';
import { DATE_FORMATS, formatDate, showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import _ from 'lodash';

const AccountDetails = props => {
  const { accountId } = props.match.params;
  const [account, setAccount] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<string>('');
  const [detailsEditable, setDetailsEditable] = useState<boolean>(false);

  const fetchAccountDetailsFn = async () => {
    setLoading('init');
    try {
      const response = await fetchAccountDetails(accountId);
      setAccount(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading('');
    }
  };

  useEffect(() => {
    fetchAccountDetailsFn();
  }, []);

  const updateAccountDetailsFn = async () => {
    setLoading('update');
    try {
      await updateAccount({
        ..._.omit(account, 'objectId'),
        id: accountId,
      });
      setDetailsEditable(false);
    } catch (e) {
      showError(e);
    } finally {
      setLoading('');
    }
  };
  
  const sendPendingPaymentEmailToAccountFn=async () => {
    setLoading('send-email');
    try {
      await sendPendingPaymentEmailToAccount(accountId);
      showSuccess('Email Sent Successfully.');
    } catch (e) {
      showError(e);
    } finally {
      setLoading('');
    }
  };
  const handleEdit = () => {
    if (detailsEditable) {
      updateAccountDetailsFn();
    } else {
      setDetailsEditable(true);
    }
  };
  
  const getAccountUsersSection = () => (
    <UsersTable
      entityId={accountId}
      entityKey={'accountId'}
      entityName={'Account'}
      addUserAPI={addUserToAccount}
      deleteUserAPI={removeUserFromAccount}
      fetchUsersAPI={fetchUsersForAccount}
    />
  );
  
  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Account Details</div>
      <BackButton />
    </div>
  );
  
  const getBasicDetails = () => (
    <div className={styles['basic-details-section']}>
      <div className={styles['details-section']}>
        <div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Account ID</div>
            <div className={`${styles['detail']} ${styles['id']}`}>{account.objectId}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Created At</div>
            <div className={styles['detail']}>{formatDate(account.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Name</div>
            {detailsEditable ? (
              <input
                className={'form-control'}
                value={account.name}
                disabled={loading === 'update'}
                onChange={e => setAccount({ ...account, name: e.target.value })}
              />
            ) : (
              <div className={styles['detail']}>{account.name}</div>
            )}
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Phone</div>
            {detailsEditable ? (
              <input
                className={'form-control'}
                value={account.phone}
                disabled={loading === 'update'}
                onChange={e => setAccount({ ...account, phone: e.target.value })}
              />
            ) : (
              <div className={styles['detail']}>{account.phone}</div>
            )}
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Address</div>
            {detailsEditable ? (
              <textarea
                className={`${styles['address-textarea']} form-control`}
                value={account.address}
                disabled={loading === 'update'}
                onChange={e => setAccount({ ...account, address: e.target.value })}
              />
            ) : (
              <div className={styles['address']}>{account.address}</div>
            )}
          </div>
        </div>
        <div>
          <ActionButton
            text={detailsEditable ? 'Save' : 'Edit'}
            active={loading !== 'update'}
            isLoading={loading === 'update'}
            onClick={handleEdit}
            customClass={'mb-3'}
          />
          <ActionButton
            text={loading === 'send-email' ? 'Sending...' : 'Send Pending Payment Email'}
            active={loading !== 'send-email'}
            isLoading={loading === 'send-email'}
            onClick={sendPendingPaymentEmailToAccountFn}
            customClass={'mb-3'}
          />
          <div className={styles['details-group']}>
            <div className={styles['label']}>Bank Account Number</div>
            {detailsEditable ? (
              <input
                className={'form-control'}
                value={account.bankAccountNo}
                disabled={loading === 'update'}
                onChange={e => setAccount({ ...account, bankAccountNo: e.target.value })}
              />
            ) : (
              <div className={styles['detail']}>{account.bankAccountNo}</div>
            )}
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Bank Routing Number</div>
            {detailsEditable ? (
              <input
                className={'form-control'}
                value={account.bankRoute}
                disabled={loading === 'update'}
                onChange={e => setAccount({ ...account, bankRoute: e.target.value })}
              />
            ) : (
              <div className={styles['detail']}>{account.bankRoute}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading==='init') return <Loading />;
  
  return (
    <div className={styles['main-container']}>
      {getHeader()}
      {getBasicDetails()}
      {getAccountUsersSection()}
    </div>
  );
};

export { AccountDetails };